import React, { memo, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom'; // Thêm import
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Box, Tooltip, IconButton, CircularProgress, Button } from '@mui/material'; // Thêm Button
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { formatDisplayValue } from './DataListItems';
import { fieldCellSx } from './fieldFormat';
import BaseFieldControl from './BaseFieldControl';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
const getContrastTextColor = (hexcolor) => {
  if (!hexcolor) return 'inherit';
  let hex = hexcolor.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
  if (hex.length !== 6) return '#000000'; 
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return yiq >= 128 ? '#000000' : '#ffffff';
};

// Màu của DÒNG ĐANG XEM (khung chi tiết bên dưới đang hiện dòng này).
const ACTIVE_ROW_BG = '#e3f2fd';
const ACTIVE_ROW_ACCENT = '#007aff';
// Dòng đang có thay đổi CHƯA LƯU (sửa thẳng trên lưới).
const DIRTY_ROW_BG = '#fff8e1';
const DIRTY_ROW_ACCENT = '#f9a825';

const MemoizedTableRow = memo(({ 
  item, tableFields, 
  isItemSelected, statusColor, globalIndex, 
  onSelectionChange, onView, onEdit, onCopy, onDelete, canEdit, canCopy, canDelete, context, history,
  renderRowActions,   // nút tuỳ chỉnh của dòng (rowActions) — vẽ đầu cột Thao tác
  // dòng ĐANG XEM ở khung chi tiết bên dưới (khác với tick chọn nhiều).
  // onRowClick(item, columnField) — columnField là `field` của cột được bấm, null nếu bấm ô dính.
  isActiveRow, onRowClick,
  // SỬA THẲNG TRÊN LƯỚI — xem inlineEdit.js
  isEditableCell,      // (field, item) => bool; null nghĩa là màn hình này không cho sửa trên lưới
  editingField,        // tên cột ĐANG mở ô nhập của CHÍNH dòng này, null nếu không phải dòng đang sửa
  isDirtyRow, isSavingRow,
  onActivateCell, onCellChange, onCommitRow, onCancelRow
}) => {
  
  const handleCheckbox = (e) => {
      if (onSelectionChange) onSelectionChange(e, item._id);
  };

  const hasTenTrangThaiColumn = tableFields.some(f => f.field === 'ten_trang_thai');

  const renderCellContent = (f, data) => {
    // === XỬ LÝ ACTION BUTTON ===
    if (f.type === 'Action') {
      const actionFn = f.actionScript;
      return (
        <Button 
          variant="outlined" 
          size="small" 
          color="primary"
          sx={{ textTransform: 'none', minWidth: '80px' }}
          onClick={(e) => {
            if(actionFn && typeof(actionFn)=="function"){
              e.stopPropagation(); // Chặn click lan ra Row
              actionFn(context, data, history);
            }
            
          }}
        >
          {f.label || 'Thực thi'}
        </Button>
      );
    }

    const displayValue = formatDisplayValue(data[f.field], f, data, context, false);
    
    if (f.type === 'RichText' && displayValue) {
      return (
        <Box 
          component="div"
          sx={{ 
            maxHeight: '60px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            fontSize: '0.875rem',
            '& p': { m: 0 }, 
            '& *': { display: 'inline', whiteSpace: 'normal' } 
          }}
          dangerouslySetInnerHTML={{ __html: displayValue }}
        />
      );
    }
    return displayValue;
  };

  // Nền của các ô DÍNH (checkbox, STT, thao tác): chúng tự tô nền đục nên phải tự đổi theo, nếu không
  // vệt sáng của dòng bị cắt cụt hai đầu.
  // Dòng CHƯA LƯU thắng dòng đang xem: người dùng cần thấy ngay chỗ mình còn nợ một cú lưu.
  const stickyBg = isDirtyRow ? DIRTY_ROW_BG : (isActiveRow ? ACTIVE_ROW_BG : (isItemSelected ? 'action.selected' : 'white'));

  return (
    <TableRow
      hover
      selected={isItemSelected}
      onClick={onRowClick ? (e) => {
        // Bấm vào nút thao tác / checkbox thì KHÔNG tính là bấm dòng: nếu không, script
        // handle_cell_clicked (vd điều hướng sang màn khác) sẽ nổ ngay khi người dùng bấm nút Sửa.
        if (e.target.closest && e.target.closest('button,input,a,[role="button"]')) return;
        // Cột nào? Ô dữ liệu có `data-field`; ô dính (checkbox/STT/thao tác) thì không có -> null.
        const cell = e.target.closest && e.target.closest('[data-field]');
        onRowClick(item, cell ? cell.getAttribute('data-field') : null);
      } : undefined}
      sx={{
        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'action.selected' },
        ...(onRowClick ? { cursor: 'pointer' } : {}),
        // DÒNG ĐANG XEM: nền xanh nhạt + vạch đậm bên trái. Phải ghi đè cả nhánh hover và nhánh
        // Mui-selected, nếu không rê chuột qua hoặc tick chọn là vệt sáng biến mất.
        ...(isActiveRow ? {
          bgcolor: ACTIVE_ROW_BG,
          '&:hover': { bgcolor: ACTIVE_ROW_BG },
          '&.Mui-selected, &.Mui-selected:hover': { bgcolor: ACTIVE_ROW_BG },
          '& > td': { borderTopColor: ACTIVE_ROW_ACCENT, borderBottomColor: ACTIVE_ROW_ACCENT },
          '& > td:first-of-type': { boxShadow: `inset 4px 0 0 0 ${ACTIVE_ROW_ACCENT}` },
        } : {}),
        // CHƯA LƯU: nền vàng nhạt + vạch cam. Đặt SAU nhánh dòng-đang-xem để thắng nó.
        ...(isDirtyRow ? {
          bgcolor: DIRTY_ROW_BG,
          '&:hover': { bgcolor: DIRTY_ROW_BG },
          '&.Mui-selected, &.Mui-selected:hover': { bgcolor: DIRTY_ROW_BG },
          '& > td': { borderTopColor: DIRTY_ROW_ACCENT, borderBottomColor: DIRTY_ROW_ACCENT },
          '& > td:first-of-type': { boxShadow: `inset 4px 0 0 0 ${DIRTY_ROW_ACCENT}` },
        } : {}),
      }}
    >
      
      {onSelectionChange && (
        <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: stickyBg, border: '1px solid #e0e0e0', borderLeft: statusColor !== 'inherit' ? `4px solid ${statusColor}` : '1px solid #e0e0e0' }}>
          <Checkbox color="primary" checked={isItemSelected} onChange={handleCheckbox} />
        </TableCell>
      )}

      <TableCell align="center" sx={{ position: 'sticky', left: onSelectionChange ? 42 : 0, zIndex: 2, bgcolor: isActiveRow ? ACTIVE_ROW_BG : (isItemSelected ? 'inherit' : 'white'), border: '1px solid #e0e0e0' }}>
        {globalIndex}
      </TableCell>

      {tableFields.map(f => {
        const isTargetTrangThai = f.field === 'trang_thai' && !hasTenTrangThaiColumn && item.ten_trang_thai != null;

        if (isTargetTrangThai) {
          const bgColor = item.color;
          const textColor = bgColor ? getContrastTextColor(bgColor) : 'inherit';
          const statusValue = item.ten_trang_thai;

          return (
            <TableCell key={f.field} data-field={f.field} align={f.align || (f.type === 'Number' ? 'right' : 'left')} sx={{ border: '1px solid #e0e0e0', ...fieldCellSx(f) }}>
              {bgColor ? (
                <Box component="span" sx={{ backgroundColor: bgColor, color: textColor, padding: '4px 8px', borderRadius: '4px', display: 'inline-block', fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {statusValue}
                </Box>
              ) : (
                statusValue
              )}
            </TableCell>
          );
        }

        // SỬA THẲNG TRÊN LƯỚI: chỉ Ô ĐANG SỬA mới mount control thật, các ô khác vẫn chỉ là chữ.
        // Đây chính là bản vá hiệu năng của lưới chi tiết, áp dụng lại cho lưới danh sách: một màn
        // 30 dòng x 3 cột liên kết mà mount đủ 90 Autocomplete thì gõ phím nào cũng giật.
        const editable = isEditableCell ? isEditableCell(f, item) : false;
        if (editable) {
          const isEditingThis = editingField === f.field;
          return (
            <TableCell
              key={f.field} data-field={f.field} data-editable="1"
              align={f.align || (f.type === 'Number' ? 'right' : 'left')}
              // Chặn nổi bọt: bấm vào ô sửa được là để SỬA, không phải để chạy script
              // handle_cell_clicked (rất hay là điều hướng sang màn khác) hay đổi dòng đang xem.
              onClick={(e) => { e.stopPropagation(); if (!isEditingThis) onActivateCell(item, f.field); }}
              sx={{ border: '1px solid #e0e0e0', p: isEditingThis ? 0.25 : undefined, ...fieldCellSx(f) }}
            >
              {isEditingThis ? (
                <Box
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onCommitRow(item); }
                    else if (e.key === 'Escape') { e.preventDefault(); onCancelRow(item); }
                  }}
                >
                  <BaseFieldControl
                    field={{ ...f, label: '', helpText: '' }}
                    value={item[f.field]}
                    onChange={(newVal) => onCellChange(item, f.field, newVal)}
                    isReadOnly={false} formData={item} masterData={item} formFields={tableFields}
                  />
                </Box>
              ) : (
                <Box
                  tabIndex={0}
                  onFocus={() => onActivateCell(item, f.field)}
                  sx={{
                    minHeight: 28, display: 'flex', alignItems: 'center', px: 0.5, borderRadius: 1,
                    cursor: 'text', borderBottom: '1px dashed', borderColor: '#b0bec5',
                    justifyContent: (f.align || (f.type === 'Number' ? 'right' : 'left')) === 'right' ? 'flex-end' : undefined,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  {renderCellContent(f, item)}
                </Box>
              )}
            </TableCell>
          );
        }

        return (
          <TableCell key={f.field} data-field={f.field} align={f.align || (f.type === 'Number' ? 'right' : 'left')} sx={{ border: '1px solid #e0e0e0', ...fieldCellSx(f) }}>
            {renderCellContent(f, item)}
          </TableCell>
        );
      })}

      <TableCell align="center" sx={{ position: 'sticky', right: 0, zIndex: 2, bgcolor: isActiveRow ? ACTIVE_ROW_BG : (isItemSelected ? 'inherit' : 'white'), border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          {/* Dòng có thay đổi chưa lưu: hiện thẳng nút Lưu/Huỷ. Rời dòng cũng tự lưu, nhưng phải có
              chỗ bấm tường minh — không thì người dùng không biết mình đang nợ một cú lưu. */}
          {isDirtyRow && (isSavingRow ? <CircularProgress size={18} sx={{ mx: 0.5 }} /> : (
            <>
              <Tooltip title="Lưu thay đổi (Enter)"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onCommitRow(item); }} sx={{ color: '#2e7d32', bgcolor: '#e8f5e9', p: 0.5 }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Tooltip title="Huỷ thay đổi (Esc)"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onCancelRow(item); }} sx={{ color: '#c62828', bgcolor: '#ffebee', p: 0.5 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            </>
          ))}
          {renderRowActions ? renderRowActions(item, 'list') : null}
          {onView && <Tooltip title="Xem chi tiết"><IconButton size="small" onClick={() => onView(item)} sx={{ color: "#007aff", bgcolor: '#e3f2fd', p: 0.5 }}><VisibilityOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
          {onEdit && canEdit && <Tooltip title="Chỉnh sửa"><IconButton size="small" onClick={() => onEdit(item)} sx={{ color: '#ff9500', bgcolor: '#fff3e0', p: 0.5 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
          {onCopy && canCopy && <Tooltip title="Nhân bản (Copy)"><IconButton size="small" onClick={() => onCopy(item)} sx={{ color: '#34c759', bgcolor: '#e8f5e9', p: 0.5 }}><ContentCopyOutlinedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
          {onDelete && canDelete && <Tooltip title="Xoá"><IconButton size="small" onClick={() => onDelete(item._id)} sx={{ color: '#ff3b30', bgcolor: '#ffebee', p: 0.5 }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
        </Box>
      </TableCell>
    </TableRow>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.isItemSelected === nextProps.isItemSelected &&
    prevProps.globalIndex === nextProps.globalIndex &&
    // THIẾU hai dòng dưới là dòng không bao giờ vẽ lại khi đổi dòng đang chọn -> vệt sáng không hiện.
    prevProps.isActiveRow === nextProps.isActiveRow &&
    prevProps.onRowClick === nextProps.onRowClick &&
    // SỬA TRÊN LƯỚI: thiếu bốn dòng dưới thì ô nhập không mở ra, vệt vàng không hiện, nút Lưu không
    // đổi trạng thái — tất cả đều CÂM LẶNG. Đúng lớp lỗi đã dính với isActiveRow.
    prevProps.editingField === nextProps.editingField &&
    prevProps.isDirtyRow === nextProps.isDirtyRow &&
    prevProps.isSavingRow === nextProps.isSavingRow &&
    prevProps.isEditableCell === nextProps.isEditableCell &&
    prevProps.renderRowActions === nextProps.renderRowActions
  );
});

export default function TableListView({ 
  data, fields, selectedIds, onSelectionChange, 
  activeRowId, onRowClick,
  onView, onEdit, onCopy, onDelete, canEdit, canCopy, canDelete, context,
  renderRowActions,
  pageIndex = 0, rowsPerPage = 20, isLoadingMore = false,
  // SỬA THẲNG TRÊN LƯỚI (xem inlineEdit.js). Không truyền `isEditableCell` = tắt hẳn tính năng.
  isEditableCell,        // (field, row) => bool
  dirtyRowIds,           // mảng id các dòng đang có thay đổi chưa lưu
  savingRowId,           // id dòng đang gửi lên server
  onCellChange,          // (row, fieldName, value)
  onCommitRow,           // (row)
  onCancelRow            // (row)
}) {
  const history = useHistory();

  // Ô đang mở control nhập: { rowId, field }. Giữ Ở ĐÂY chứ không đẩy lên DataListManager, để mỗi lần
  // bấm sang ô khác không kéo theo cả màn hình vẽ lại.
  const [editingCell, setEditingCell] = React.useState(null);
  const rowIdOf = (r) => (r && (r._id || r.id)) ?? null;

  // Tham chiếu MỚI NHẤT trong ref -> các callback truyền xuống dòng KHÔNG đổi identity (memo còn tác dụng).
  const cbRef = useRef(null);
  cbRef.current = { onCellChange, onCommitRow, onCancelRow, isEditableCell };
  const dataRef = useRef(data); dataRef.current = data;

  const stableIsEditableCell = useCallback(
    (f, row) => (cbRef.current.isEditableCell ? cbRef.current.isEditableCell(f, row) : false), []);

  // Ô đang sửa cũng giữ trong ref: `activateCell` phải đọc được giá trị hiện tại mà KHÔNG nhận nó làm
  // dependency (nhận là hàm đổi identity mỗi lần bấm ô -> memo của mọi dòng vô hiệu).
  const editingCellRef = useRef(editingCell); editingCellRef.current = editingCell;

  const activateCell = useCallback((row, fieldName) => {
    const id = rowIdOf(row);
    const prev = editingCellRef.current;
    if (prev && prev.rowId === id && prev.field === fieldName) return;
    // ĐỔI SANG DÒNG KHÁC = rời dòng cũ => lưu dòng cũ. Đây là toàn bộ hợp đồng "lưu khi rời dòng";
    // thiếu chỗ này thì thay đổi treo lơ lửng cho tới khi người dùng bấm nút Lưu.
    // Việc lưu phải nằm NGOÀI hàm cập nhật state: React StrictMode chạy hàm cập nhật hai lần, để
    // trong đó là bắn hai lệnh PUT cho cùng một dòng.
    if (prev && prev.rowId !== id && cbRef.current.onCommitRow) {
      const prevRow = (dataRef.current || []).find(r => rowIdOf(r) === prev.rowId);
      if (prevRow) cbRef.current.onCommitRow(prevRow);
    }
    setEditingCell({ rowId: id, field: fieldName });
  }, []);

  const handleCellChange = useCallback((row, fieldName, value) => {
    if (cbRef.current.onCellChange) cbRef.current.onCellChange(row, fieldName, value);
  }, []);
  const handleCommitRow = useCallback((row) => {
    setEditingCell(null);
    if (cbRef.current.onCommitRow) cbRef.current.onCommitRow(row);
  }, []);
  const handleCancelRow = useCallback((row) => {
    setEditingCell(null);
    if (cbRef.current.onCancelRow) cbRef.current.onCancelRow(row);
  }, []);

  const dirtySet = React.useMemo(() => new Set(dirtyRowIds || []), [dirtyRowIds]);
  const tableFields = fields.filter(f => !['color', 'picture', 'hinh_anh'].includes(f.field) && f.hideInList !== true).sort((a, b) => (a.listOrder ?? 9999) - (b.listOrder ?? 9999));

  const selectedIdsRef = useRef(selectedIds);
  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  const handleSelectAllClick = useCallback((event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n._id);
      if (onSelectionChange) onSelectionChange([...new Set([...selectedIdsRef.current, ...newSelecteds])]);
      return;
    }
    const pageIds = data.map(n => n._id);
    if (onSelectionChange) onSelectionChange(selectedIdsRef.current.filter(id => !pageIds.includes(id)));
  }, [data, onSelectionChange]);

  const handleToggleSingleRow = useCallback((event, id) => {
    const currentSelected = selectedIdsRef.current; 
    const selectedIndex = currentSelected.indexOf(id);
    let newSelected = [];
    
    if (selectedIndex === -1) newSelected = newSelected.concat(currentSelected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(currentSelected.slice(1));
    else if (selectedIndex === currentSelected.length - 1) newSelected = newSelected.concat(currentSelected.slice(0, -1));
    else if (selectedIndex > 0) newSelected = newSelected.concat(currentSelected.slice(0, selectedIndex), currentSelected.slice(selectedIndex + 1));
    
    if (onSelectionChange) onSelectionChange(newSelected);
  }, [onSelectionChange]);

  const numSelectedThisPage = data.filter(item => (selectedIds || []).includes(item._id)).length;

  return (
    <TableContainer sx={{ flexGrow: 1, overflowX: 'auto', overflowY: 'auto', minHeight: 0 }}>
      <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
        <TableHead>
          <TableRow>

            {!!onSelectionChange && <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, top: 0, zIndex: 4, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc' }}>
              <Checkbox color="primary" indeterminate={numSelectedThisPage > 0 && numSelectedThisPage < data.length} checked={data.length > 0 && numSelectedThisPage === data.length} onChange={handleSelectAllClick} />
            </TableCell>}

            <TableCell align="center" sx={{ position: 'sticky', left: 42, top: 0, zIndex: 4, width: 50, fontWeight: 'bold', bgcolor: '#e3f2fd', border: '1px solid #cfd8dc' }}>STT</TableCell>
            {tableFields.map(f => (
              <TableCell key={f.field} align={f.align || (f.type === 'Number' ? 'right' : 'left')} sx={{ fontWeight: 'bold', bgcolor: '#f8f9fa', border: '1px solid #cfd8dc', whiteSpace: 'nowrap', ...(f.tableWidth ? { minWidth: f.tableWidth } : {}) }}>{f.label}</TableCell>
            ))}
            <TableCell align="center" sx={{ position: 'sticky', right: 0, top: 0, zIndex: 4, fontWeight: 'bold', bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', minWidth: 120, whiteSpace: 'nowrap' }}>Thao tác</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoadingMore ? (
             <TableRow><TableCell colSpan={100} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
          ) : data.length === 0 ? (
             <TableRow><TableCell colSpan={100} align="center" sx={{ py: 4, color: 'text.secondary' }}>Chưa có dữ liệu</TableCell></TableRow>
          ) : (
            data.map((item, index) => {
                const isItemSelected = (selectedIds || []).indexOf(item._id) !== -1;
                const statusColor = item.color || 'inherit';
                const globalIndex = pageIndex * rowsPerPage + index + 1;
                return (
                  <MemoizedTableRow 
                    key={item._id || index}
                    item={item} 
                    tableFields={tableFields} 
                    isItemSelected={isItemSelected} 
                    statusColor={statusColor} 
                    globalIndex={globalIndex} 
                    onSelectionChange={onSelectionChange ? handleToggleSingleRow : null} 
                    onView={onView} 
                    onEdit={onEdit}
                    canEdit={canEdit}
                    onCopy={onCopy}
                    canCopy={canCopy}
                    onDelete={onDelete}
                    canDelete={canDelete}
                    context={context}
                    renderRowActions={renderRowActions}
                    isActiveRow={!!activeRowId && (item._id || item.id) === activeRowId}
                    onRowClick={onRowClick}
                    isEditableCell={isEditableCell ? stableIsEditableCell : null}
                    editingField={editingCell && editingCell.rowId === (item._id || item.id) ? editingCell.field : null}
                    isDirtyRow={dirtySet.has(item._id || item.id)}
                    isSavingRow={!!savingRowId && savingRowId === (item._id || item.id)}
                    onActivateCell={activateCell}
                    onCellChange={handleCellChange}
                    onCommitRow={handleCommitRow}
                    onCancelRow={handleCancelRow}
                    history={history} // Truyền history xuống
                  />
                );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}